'use client';

import { mergeProps, useObjectRef } from '@react-aria/utils';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import type { AriaLabelingProps, CommonProps } from '../types.js';
import { ColumnCollectionProvider } from './contexts/column-collection.js';
import { TableFocusProvider } from './contexts/focus.js';
import { type RowSelectionMode, TableStateProvider, useTableState } from './contexts/table-state.js';
import { useTable } from './hooks/use-table.js';

export interface TableProps extends CommonProps, AriaLabelingProps {
  /** The contents of the table. */
  children: ReactNode;
  /**
   * The number of rows in the full dataset, including headers.
   *
   * Provide this value if your table presents a subset of rows, such as when using pagination.
   * Use `-1` if the number of rows is not known.
   *
   * You must provide `rowNumber` on each table row if `totalRowCount` is defined.
   */
  totalRowCount?: number;
  /**
   * The number of columns in the full dataset.
   *
   * Provide this value if your table presents a subset of columns, such as when the user toggles column visibility.
   * Use `-1` if the number of columns is not known.
   *
   * You must provide `columnNumber` on each table header cell if `totalColumnCount` is defined.
   */
  totalColumnCount?: number;
  /**
   * The type of row selection that is allowed within the table.
   * @default 'none'
   */
  rowSelectionMode?: RowSelectionMode;
  /**
   * Whether all of the table's rows are selected.
   * The meaning of "all rows" is dictated by the consumer.
   */
  areAllRowsSelected?: boolean;
  /**
   * Whether some (but not all) of the table's rows are selected.
   * The meaning of "some rows" is dictated by the consumer.
   */
  areSomeRowsSelected?: boolean;
  /**
   * A function called when the selection state of all rows should change.
   *
   * If called without an argument, you are free to pick the new selection state based on your own logic.
   * If called with an argument, you must set the selection state to the provided value.
   */
  onAllRowsSelectionToggle?: (value?: boolean) => void;
}

function Table(
  {
    rowSelectionMode = 'none',
    areAllRowsSelected,
    areSomeRowsSelected,
    onAllRowsSelectionToggle,
    ...props
  }: TableProps,
  ref: ForwardedRef<HTMLTableElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!ariaLabel && !ariaLabelledBy) {
      console.warn('Table requires one of aria-label / aria-labelledby for accessibility');
    }
  }, [ariaLabel, ariaLabelledBy]);

  return (
    <TableFocusProvider>
      <TableStateProvider
        rowSelectionMode={rowSelectionMode}
        areAllRowsSelected={areAllRowsSelected}
        areSomeRowsSelected={areSomeRowsSelected}
        onAllRowsSelectionToggle={onAllRowsSelectionToggle}
      >
        <ColumnCollectionProvider>
          <span tabIndex={-1} data-cim-table-guard aria-hidden />
          <TableInner props={props} forwardedRef={ref} />
          <span tabIndex={-1} data-cim-table-guard aria-hidden />
        </ColumnCollectionProvider>
      </TableStateProvider>
    </TableFocusProvider>
  );
}

interface TableInnerProps {
  props: Omit<
    TableProps,
    'rowSelectionMode' | 'areAllRowsSelected' | 'areSomeRowsSelected' | 'onAllRowsSelectionToggle'
  >;
  forwardedRef: ForwardedRef<HTMLTableElement>;
}

function TableInner({
  props: { children, UNSAFE_className, UNSAFE_style, totalRowCount, totalColumnCount, ...props },
  forwardedRef,
}: TableInnerProps) {
  const tableRef = useObjectRef(forwardedRef);
  const { tableProps } = useTable(tableRef);
  const { rowSelectionMode } = useTableState();

  return (
    <table
      {...mergeProps(props, tableProps)}
      ref={tableRef}
      className={UNSAFE_className}
      style={UNSAFE_style}
      aria-rowcount={totalRowCount}
      aria-colcount={totalColumnCount}
      aria-multiselectable={rowSelectionMode === 'multiple' ? true : undefined}
    >
      {children}
    </table>
  );
}

/**
 * Displays data in an organized, tabular way.
 *
 * See [table usage guidelines](https://ui.cimpress.io/components/table/) and [table building guide](https://ui.cimpress.io/dev-guides/tables/).
 */
const _Table = forwardRef(Table, 'Table');

export { _Table as Table };
